import type { SearchQuery } from '@maestro/data-layer'

import { theme } from '@maestro/local/theme'

export function normalizeFilters(
  filters?: string | string[] | (string | string[])[]
): (string | string[])[] {
  if (!filters) {
    return []
  }

  const normalized: (string | string[])[] = []
  if (filters) {
    if (Array.isArray(filters)) {
      // Flatten nested arrays (if any)
      filters.forEach((f) => {
        if (Array.isArray(f)) {
          normalized.push([...f])
        } else {
          normalized.push(f)
        }
      })
    } else {
      normalized.push(filters)
    }
  }

  return normalized
}

export function addCategoryFilter(
  categoryId?: string,
  filters?: string | string[] | (string | string[])[]
): (string | string[])[] | undefined {
  const { categoryFilterKey } = theme().search

  if (!categoryId) {
    return filters as (string | string[])[] | undefined
  }
  const normalized = normalizeFilters(filters)
  normalized.push(`${categoryFilterKey}:${decodeURIComponent(categoryId)}`)

  return normalized
}

export function addBrandFilter(
  brandId?: string,
  filters?: string | string[] | (string | string[])[]
): (string | string[])[] | undefined {
  const { brandFilterKey } = theme().search

  if (!brandId) {
    return filters as (string | string[])[] | undefined
  }

  const normalized = normalizeFilters(filters)
  normalized.push(`${brandFilterKey}:${decodeURIComponent(brandId)}`)
  return normalized
}

export function addCampaignFilter(
  campaignId?: string,
  filters?: string | string[] | (string | string[])[]
): (string | string[])[] | undefined {
  const { campaignFilterKey } = theme().search

  if (!campaignId) {
    return filters as (string | string[])[] | undefined
  }

  const normalized = normalizeFilters(filters)
  normalized.push(`${campaignFilterKey}:${decodeURIComponent(campaignId)}`)

  return normalized
}

export function addCampaignsOverviewFilter(
  filters?: string | string[] | (string | string[])[]
): (string | string[])[] | undefined {
  const { campaignsOverviewFilterKey } = theme().search

  const normalized = normalizeFilters(filters)
  normalized.push(campaignsOverviewFilterKey)

  return normalized
}

export function extractFilterValue(facetFilter: unknown): string {
  if (typeof facetFilter !== 'string' || !facetFilter.includes(':')) {
    return ''
  }
  return facetFilter.split(':')[1] || ''
}

export function extractCategoryId(facetFilter: unknown): string {
  return extractFilterValue(facetFilter)
}

/**
 * Encodes a SearchQuery into a clean URL format
 * Format: /search?text={text}&{filters}&sort={sortBy}&page={page}&type={type}&category={categoryValue}
 */
export function encodeSearchUrl(
  searchQuery: SearchQuery,
  basePath = '/search'
): string {
  const {
    seoKeys,
    categoryFilter: { hierarchicalCategoryFacetKey },
  } = theme().filter

  const params = new URLSearchParams()

  if (searchQuery.text) {
    params.set(seoKeys.text, searchQuery.text)
  }

  // Add hierarchical category
  const categoryValue = searchQuery[hierarchicalCategoryFacetKey] as string
  if (categoryValue) {
    const prettyCategory = decodeURIComponent(categoryValue)
      .replace('+', '%2B')
      .replace(',', '%2C')
      .replace(':', '%3A')
      .replace(/\s*\/\/\/\s*/g, ':')
      .replace(/\s*\|\|\s*/g, ',')
    params.set(seoKeys.category, prettyCategory)
  }

  if (searchQuery.filters) {
    const rangeRegex = /([^\s()]+):(\d+(?:\.\d+)?)\s+TO\s+(\d+(?:\.\d+)?)/g
    const decoded = decodeURIComponent(searchQuery.filters)
    let match
    while ((match = rangeRegex.exec(decoded)) !== null) {
      const [, attribute, min, max] = match
      params.set(attribute, `${min},${max}`)
    }
  }

  if (searchQuery.facetFilters) {
    const facetMap = new Map<string, string[]>()

    searchQuery.facetFilters.forEach((group) => {
      if (Array.isArray(group)) {
        group.forEach((filter) => {
          if (typeof filter === 'string' && filter.includes(':')) {
            const [attribute, value] = filter.split(':', 2)
            if (!facetMap.has(attribute)) {
              facetMap.set(attribute, [])
            }
            facetMap.get(attribute)!.push(encodeURIComponent(value))
          }
        })
      } else if (typeof group === 'string' && group.includes(':')) {
        const [attribute, value] = group.split(':', 2)
        if (!facetMap.has(attribute)) {
          facetMap.set(attribute, [])
        }
        facetMap.get(attribute)!.push(encodeURIComponent(value))
      }
    })

    facetMap.forEach((values, attribute) => {
      params.set(attribute, values.join(','))
    })
  }

  // Add sorting
  if (searchQuery.sortBy) {
    params.set(seoKeys.sortBy, searchQuery.sortBy)
  }

  // Add pagination
  if (searchQuery.page && searchQuery.page > 1) {
    params.set('page', searchQuery.page.toString())
  }

  // Add entity type
  if (searchQuery.type) {
    params.set('type', searchQuery.type)
  }

  // Add any additional parameters (e.g., _gl for tracking)
  if (searchQuery._gl) {
    params.set('_gl', searchQuery._gl as string)
  }

  // Build final URL
  const queryString = decodeURIComponent(params.toString()).toLowerCase()
  return queryString ? `${basePath}?${queryString}` : basePath
}

/**
 * Decodes a clean URL format back into a SearchQuery
 * Format: /search?text={text}&{filters}&sort={sortBy}&page={page}&type={type}&category={categoryValue}
 */
export function decodeSearchUrl(searchParams: URLSearchParams): SearchQuery {
  const {
    seoKeys,
    categoryFilter: { hierarchicalCategoryFacetKey },
  } = theme().filter

  const query: SearchQuery = {}

  const rangeFilters: string[] = []
  const facetFilters: string[][] = []

  searchParams.forEach((value, key) => {
    switch (key) {
      case seoKeys.text:
        query.text = value
        break
      case seoKeys.sortBy:
        query.sortBy = value
        break
      case 'page':
        query.page = parseInt(value, 10) || 1
        break
      case 'type':
        query.type = value
        break
      case seoKeys.category:
        query[hierarchicalCategoryFacetKey as keyof SearchQuery] = value
          .replace(/:/g, ' /// ')
          .replace(/,/g, '||')
        break
      case '_gl':
        query._gl = value
        break
      default:
        // Check if it's a range filter
        if (
          value.includes(',') &&
          /^\d+(?:\.\d+)?,\d+(?:\.\d+)?$/.test(value)
        ) {
          const [min, max] = value.split(',')
          rangeFilters.push(`${key}:${min} TO ${max}`)
        } else {
          // It's a facet filter
          const values = value.split(',').map((v) => `${key}:${v}`)
          facetFilters.push(values)
        }
        break
    }
  })

  if (rangeFilters.length > 0) {
    query.filters = rangeFilters.join(' AND ')
  }

  if (facetFilters.length > 0) {
    query.facetFilters = facetFilters
  }

  return query
}

function normalizeSearchOptionIdPart(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function getSearchOptionId(
  section: string,
  value: string,
  index: number
): string {
  const normalizedSection = normalizeSearchOptionIdPart(section) || 'section'
  const normalizedValue = normalizeSearchOptionIdPart(value) || 'option'

  return `searchbar-option-${normalizedSection}-${normalizedValue}-${index}`
}
